/**
 * @thepit/i18n — Copys de UI (SSOT S8).
 *
 * Regla: ningún string visible se escribe hardcodeado en componentes; todos salen de
 * aquí. La regla de lint `@thepit/no-hardcoded-string` lo verifica.
 */
import es from '../es.json' with { type: 'json' };

export type Locale = 'es';

const CATALOGS: Record<Locale, Record<string, unknown>> = {
  es: es as unknown as Record<string, unknown>,
};

export const DEFAULT_LOCALE: Locale = 'es';

/** Punto de acceso al catálogo completo (para tests de paridad). */
export function catalog(locale: Locale = DEFAULT_LOCALE): Record<string, unknown> {
  return CATALOGS[locale];
}

/** Aplana el catálogo a claves con punto: { app: { name } } → 'app.name'. */
export function flatten(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k.startsWith('$')) continue;
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(out, flatten(v as Record<string, unknown>, key));
    } else if (typeof v === 'string') {
      out[key] = v;
    }
  }
  return out;
}

/** Todas las claves disponibles, aplanadas. */
export function allKeys(locale: Locale = DEFAULT_LOCALE): string[] {
  return Object.keys(flatten(catalog(locale)));
}

export type TranslationKey = string;

/** Interpola `{var}` en un texto. */
export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_m, name: string) =>
    name in vars ? String(vars[name]) : `{${name}}`,
  );
}

/**
 * Traduce una clave. Si falta, devuelve la clave entre `⟦⟧` (nunca texto vacío) para
 * que sea visible en desarrollo y detectable en tests.
 */
export function t(
  key: TranslationKey,
  vars?: Record<string, string | number>,
  locale: Locale = DEFAULT_LOCALE,
): string {
  const flat = flatten(catalog(locale));
  const value = flat[key];
  if (value === undefined) return `⟦${key}⟧`;
  return interpolate(value, vars);
}

/** Igual que `t` pero sin fallback visible: null si no existe (para tests). */
export function tryT(key: string, locale: Locale = DEFAULT_LOCALE): string | null {
  const flat = flatten(catalog(locale));
  return flat[key] ?? null;
}
